import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  FileText,
  Ban,
  ClipboardList,
  Usb,
  Inbox,
  Users,
  Fingerprint,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  X,
  Check,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { hasAnyCapability, type Capability } from '@/lib/rbac';
import { useTheme } from '@/hooks/use-theme';
import { useTranslation } from 'react-i18next';
import { setLocale, type AppLocale } from '@/i18n';

const STORAGE_KEY = 'license-dashboard.sidebar';

const navItems: {
  labelKey: string;
  path: string;
  icon: typeof Home;
  capability: Capability | readonly Capability[];
}[] = [
  { labelKey: 'shell.nav.overview', path: '/', icon: Home, capability: 'viewOverview' },
  {
    labelKey: 'shell.nav.licenses',
    path: '/licenses',
    icon: FileText,
    capability: 'manageLicenses',
  },
  {
    labelKey: 'shell.nav.factoryUsb',
    path: '/factory-usb',
    icon: Usb,
    capability: 'manageLicenses',
  },
  { labelKey: 'shell.nav.blacklist', path: '/blacklist', icon: Ban, capability: 'viewBlacklist' },
  { labelKey: 'shell.nav.audit', path: '/audit', icon: ClipboardList, capability: 'viewAudit' },
  {
    labelKey: 'shell.nav.requests',
    path: '/requests',
    icon: Inbox,
    capability: ['submitLicenseRequests', 'reviewLicenseRequests'],
  },
  { labelKey: 'shell.nav.operators', path: '/operators', icon: Users, capability: 'manageUsers' },
  {
    labelKey: 'shell.nav.fingerprintRequests',
    path: '/fingerprint-requests',
    icon: Fingerprint,
    capability: 'manageUsers',
  },
];

const LANGUAGE_OPTIONS: { value: AppLocale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Русский' },
];

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });
  const { setTheme } = useTheme();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen, onClose]);

  const handleSignOut = async () => {
    await signOut();
    toast.success(t('shell.signedOut'));
  };

  const initials = (user?.username ?? '?').slice(0, 2).toUpperCase();
  const roleLabel = user ? t(`shell.roles.${user.role}`) : '';
  const visibleNavItems = user
    ? navItems.filter((item) =>
        hasAnyCapability(
          user.role,
          Array.isArray(item.capability) ? item.capability : [item.capability as Capability]
        )
      )
    : [];

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity lg:hidden',
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        aria-hidden="true"
        onClick={onClose}
      />

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r bg-sidebar transition-all duration-300',
          'lg:static lg:z-auto lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'lg:w-16' : 'lg:w-[236px]',
          'w-[236px]'
        )}
      >
        <div
          className={cn('flex items-center justify-between px-6 pb-6 pt-7', collapsed && 'lg:px-4')}
        >
          <Link to="/" className="flex items-center gap-2.5" onClick={onClose}>
            <span className="h-2.5 w-2.5 rounded-[3px] bg-primary" aria-hidden="true" />
            {!collapsed && (
              <span className="text-[19px] font-semibold tracking-tight text-foreground">
                License
              </span>
            )}
          </Link>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground lg:hidden"
            aria-label={t('shell.closeMenu')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 px-3.5">
          {visibleNavItems.map((item) => {
            const isActive =
              item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-[14.5px] font-medium transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
                )}
                title={collapsed ? t(item.labelKey) : undefined}
              >
                <item.icon
                  className={cn('h-[17px] w-[17px] shrink-0', isActive && 'text-primary')}
                />
                {!collapsed && <span>{t(item.labelKey)}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="border-t px-3.5 pb-4 pt-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'flex w-full items-center gap-3 rounded-[9px] px-3 py-2 text-left transition-colors hover:bg-foreground/5',
                  collapsed && 'lg:justify-center lg:px-0'
                )}
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
                  {initials}
                </span>
                {!collapsed && (
                  <>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-medium text-foreground">
                        {user?.username}
                      </span>
                      <span className="block truncate text-xs text-faint">{roleLabel}</span>
                    </span>
                    <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-faint" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-44">
              <DropdownMenuLabel>{t('shell.account')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>{t('shell.theme')}</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => setTheme('light')}>
                    {t('shell.themeLight')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('dark')}>
                    {t('shell.themeDark')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('system')}>
                    {t('shell.themeSystem')}
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>{t('shell.language')}</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <DropdownMenuItem key={opt.value} onClick={() => void setLocale(opt.value)}>
                      <span className="flex-1">{opt.label}</span>
                      {i18n.language.startsWith(opt.value) && <Check className="h-3.5 w-3.5" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => void navigate('/change-password')}>
                {t('shell.changePassword')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => void navigate('/api-keys')}>
                {t('shell.apiKeys')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => void handleSignOut()}>
                {t('shell.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={() => setCollapsed((c) => !c)}
            className="mt-1 hidden w-full items-center gap-3 rounded-[9px] px-3 py-2 text-[13px] font-medium text-faint transition-colors hover:bg-foreground/5 hover:text-muted-foreground lg:flex"
            title={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronLeft className="h-4 w-4 shrink-0" />
            )}
            {!collapsed && <span>{t('shell.collapse')}</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
